<template>
  <div class="lock-wrapper">
    <div
      v-if="!canEditLockedSections && isSectionLocked"
      class="button-wrapper"
    >
      <TNote style="max-width: 190px; text-align: center">{{
        $t("forms.section_locked")
      }}</TNote>

      <t-button
        data-testid="structure-locked-button"
        disabled
        style="margin-top: 12px"
      >
        <template #icon>
          <svg
            class="icon-style"
            fill="currentColor"
            viewBox="0 0 20 20"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              fill-rule="evenodd"
              d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z"
              clip-rule="evenodd"
            ></path>
          </svg>
        </template>
        {{ $t("forms.unlock_section") }}
      </t-button>
    </div>

    <div
      v-if="
        isSynced &&
        selectedSection &&
        savedBlocksStore.editingRowUid !== selectedSection.uid
      "
      class="button-wrapper"
    >
      <TNote style="max-width: 190px; text-align: center">{{
        $t("forms.section_synced")
      }}</TNote>
    </div>

    <div
      :class="{
        locked:
          (!canEditLockedSections && isSectionLocked) ||
          (isSynced &&
            selectedSection &&
            savedBlocksStore.editingRowUid !== selectedSection.uid),
      }"
    >
      <div>
        <menu-content-container
          :chip="$t('forms.structure_properties')"
          :border-bottom="false"
        >
          <template v-if="enableSectionVariants" #header-action>
            <TButton data-testid="open-variant" @click="openVariants()">
              <template #icon>
                <Variants />
              </template>
              {{ $t("variants.variants") }}
            </TButton>
          </template>
        </menu-content-container>

        <menu-content-container border-full>
          <content-row>
            <t-input-number
              v-model.number="colsCount"
              data-testid="section-cols-input-number"
              :label="$t('forms.section_cols')"
              unit=""
              :min="1"
              :max="8"
              regex-pattern="[1-8]{1}"
            ></t-input-number>
            <color-picker
              data-testid="section-background-color"
              position="right"
              :label="$t('forms.section_bgc')"
              :default-color="bgColor.model"
              :changed="bgColor.changed"
              :reset-tooltip="$t('forms.reset_value')"
              @color-change="bgColor.model = $event"
              @reset="bgColor.reset"
            ></color-picker>
          </content-row>
        </menu-content-container>

        <menu-content-container border-full>
          <TLabel :changed="fullWidth.changed" @reset="fullWidth.reset">{{
            $t("forms.structure_width")
          }}</TLabel>
          <content-row justify-between>
            <TButtonGroup>
              <TGroupButton
                data-testid="column-width"
                data="column"
                small
                :active="fullWidth.model !== 'full-width'"
                @button-clicked="fullWidth.model = false"
              >
                <ColumnWidth />
              </TGroupButton>
              <TGroupButton
                data-testid="full-width"
                data="full"
                small
                :active="fullWidth.model === 'full-width'"
                @button-clicked="fullWidth.model = 'full-width'"
              >
                <FullWidth />
              </TGroupButton>
            </TButtonGroup>

            <t-switch
              v-model="stuckOnMobile"
              data-testid="do-not-stack-on-mobile-switch"
              :label="$t('forms.stuck')"
              :changed="inGroup.changed"
              @reset="inGroup.reset"
            ></t-switch>
          </content-row>
        </menu-content-container>

        <borderRadiusForm
          data-testid="image-border-radius"
          :label="$t('forms.border_radius')"
          external-usage
          :external-border-radius="borderRadius.model"
          :changed="borderRadius.changed"
          :default-value="borderRadius.defaultValue"
          @borderRadiusChanged="borderRadius.model = $event"
          @reset="borderRadius.reset"
        ></borderRadiusForm>
        <border-form
          data-testid="section-border-form"
          :header-title="$t('forms.structure_border')"
          :border="border.model"
          :default-border="border.defaultValue"
          :default-border-top="borderTop.defaultValue"
          :default-border-right="borderRight.defaultValue"
          :default-border-bottom="borderBottom.defaultValue"
          :default-border-left="borderLeft.defaultValue"
          :border-top="borderTop.model"
          :border-bottom="borderBottom.model"
          :border-left="borderLeft.model"
          :border-right="borderRight.model"
          :changed="borderAnyChanged"
          :changed-top="borderTop.changed"
          :changed-right="borderRight.changed"
          :changed-bottom="borderBottom.changed"
          :changed-left="borderLeft.changed"
          @borderChanged="onBorderChanged"
          @reset="onBorderReset"
          @reset-top="borderTop.reset"
          @reset-right="borderRight.reset"
          @reset-bottom="borderBottom.reset"
          @reset-left="borderLeft.reset"
        ></border-form>

        <padding-form
          data-testid="section-padding-form"
          external-usage
          :external-padding="padding.model"
          :label="$t('forms.structure_margin')"
          :changed="padding.changed"
          :default-value="padding.defaultValue"
          @paddingChanged="padding.model = $event"
          @reset="padding.reset"
        ></padding-form>

        <menu-content-container border-full>
          <TLabel
            :changed="backgroundImage.changed || gradient.changed"
            :tooltip="$t('forms.reset_value')"
            @reset="onBackgroundReset"
            >{{ $t("forms.section_bgi") }}</TLabel
          >
          <content-row
            :margin-top="8"
            class="gap-y-2"
            style="justify-content: flex-start; flex-wrap: wrap"
          >
            <ImageButtons
              v-model="backgroundImage.model"
              :show-remove="!setDynamic"
              show-replace
              :class="{ 'mb-2': !setDynamic && backgroundImage.model }"
            />
            <TButton
              data-testid="image-set-dynamic"
              @click="setDynamic = !setDynamic"
            >
              <template #icon>
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M15.4375 12.5625C15.7422 12.5625 16 12.8203 16 13.125C16 13.4531 15.7422 13.6875 15.4375 13.6875H7.65625C7.42188 14.4609 6.69531 15 5.875 15C4.82031 15 4 14.1797 4 13.125C4 12.0938 4.82031 11.25 5.875 11.25C6.69531 11.25 7.42188 11.8125 7.65625 12.5625H15.4375ZM5.125 13.125C5.125 13.5469 5.45312 13.875 5.875 13.875C6.27344 13.875 6.625 13.5469 6.625 13.125C6.625 12.7266 6.27344 12.375 5.875 12.375C5.45312 12.375 5.125 12.7266 5.125 13.125ZM12.3203 7.3125C12.5547 6.5625 13.2812 6 14.125 6C15.1562 6 16 6.84375 16 7.875C16 8.92969 15.1562 9.75 14.125 9.75C13.2812 9.75 12.5547 9.21094 12.3203 8.4375H4.5625C4.23438 8.4375 4 8.20312 4 7.875C4 7.57031 4.23438 7.3125 4.5625 7.3125H12.3203ZM14.125 8.625C14.5234 8.625 14.875 8.29688 14.875 7.875C14.875 7.47656 14.5234 7.125 14.125 7.125C13.7031 7.125 13.375 7.47656 13.375 7.875C13.375 8.29688 13.7031 8.625 14.125 8.625Z"
                    fill="currentColor"
                  />
                </svg>
              </template>
              {{ $t("forms.image_set_dynamic") }}
            </TButton>
            <TButton
              v-if="!gradient.model"
              data-testid="add-gradient"
              @click="addGradient"
            >
              <template #icon>
                <svg
                  width="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M19.958 3C20.3669 3 20.7597 3.16205 21.0488 3.45117C21.3379 3.74029 21.5 4.13312 21.5 4.54199V19.958C21.5 20.3669 21.3379 20.7597 21.0488 21.0488C20.7597 21.3379 20.3669 21.5 19.958 21.5H4.54199C4.13312 21.5 3.74029 21.3379 3.45117 21.0488C3.16205 20.7597 3 20.3669 3 19.958V4.54199C3 4.13312 3.16205 3.74029 3.45117 3.45117C3.74029 3.16205 4.13312 3 4.54199 3H19.958ZM10.708 4.54199V7.625H7.625V10.708H10.708V13.792H7.625V16.875H10.708V19.958H13.792V16.875H16.875V19.958H19.958V4.54199H16.875V7.625H13.792V4.54199H10.708ZM13.792 16.875H10.708V13.792H13.792V16.875ZM16.875 13.792H13.792V10.708H16.875V13.792ZM13.792 10.708H10.708V7.625H13.792V10.708Z"
                    fill="currentColor"
                  />
                </svg>
              </template>
              {{ $t("forms.add_gradient") }}
            </TButton>
          </content-row>
          <content-row class="!block" data-testid="gradient-form">
            <TTabsToggle
              v-if="gradient.model"
              :header="$t('forms.gradient')"
              :changed="gradient.changed"
              default-open
              no-move
              show-remove
              can-be-reset
              @remove-block="gradient.model = undefined"
              @reset="gradient.reset"
            >
              <menu-content-container border-full :padding-left-right="8">
                <TGradient v-model="gradient.model" />
              </menu-content-container>
            </TTabsToggle>
          </content-row>

          <content-row v-if="setDynamic">
            <t-auto-complete
              v-model="backgroundImage.model"
              data-testid="image-dynamic-src"
              :label="`${$t('forms.image_url')} / ${$t('forms.dynamic_image')}`"
              placeholder="Some placeholder"
            ></t-auto-complete>
          </content-row>

          <content-row v-if="backgroundImage.model" justify-between>
            <TDropdown
              data-testid="section-size-options"
              :label="$t('forms.image_size_options_label')"
              :selected="
                backgroundImageSize.model
                  ? $t('forms.image_size_options.' + backgroundImageSize.model)
                  : undefined
              "
              rounded-full
              :data="[
                { value: 'auto', label: $t('forms.image_size_options.auto') },
                { value: 'cover', label: $t('forms.image_size_options.cover') },
                {
                  value: 'contain',
                  label: $t('forms.image_size_options.contain'),
                },
              ]"
              :changed="backgroundImageSize.changed"
              @selection-changed="
                (val: TDropdownOption) =>
                  (backgroundImageSize.model = val.value)
              "
              @reset="backgroundImageSize.reset"
            ></TDropdown>
            <t-switch
              v-model="backgroundRepeat"
              class="mt-6"
              data-testid="section-repeat-switch"
              :label="$t('forms.repeat_background')"
              :changed="backgroundRepeatBinding.changed"
              @reset="backgroundRepeatBinding.reset"
            ></t-switch>
          </content-row>

          <content-row :margin-bottom="16">
            <t-note>{{ $t("layout.background_image_text") }}</t-note>
          </content-row>
        </menu-content-container>

        <menu-content-container border-full>
          <content-row>
            <hide-on-mobile></hide-on-mobile>
          </content-row>

          <content-row :margin-top="16">
            <hide-on-desktop></hide-on-desktop>
          </content-row>

          <content-row v-if="rolesStore.arePermissionsEnabled">
            <TSwitch
              v-model="isSectionLocked"
              data-testid="structure-lock-switch"
              :disabled="!canLockSections"
              :label="$t('forms.lock_section')"
              :tooltip="$t('forms.cannot_unlock_section')"
            ></TSwitch>
          </content-row>
        </menu-content-container>

        <menu-content-container
          :header="$t('forms.column_properties')"
          :border-bottom="false"
        >
          <TButtonGroup v-if="colsCount > 1" :label="$t('forms.image_align')">
            <TGroupButton
              data="top"
              data-testid="align-top"
              small
              :active="valign === 'top'"
              @button-clicked="valign = 'top'"
            >
              <AligmentTop />
            </TGroupButton>
            <TGroupButton
              data="middle"
              data-testid="align-middle"
              small
              :active="valign === 'middle'"
              @button-clicked="valign = 'middle'"
            >
              <AligmentMiddle />
            </TGroupButton>
            <TGroupButton
              data="bottom"
              data-testid="align-bottom"
              small
              :active="valign === 'bottom'"
              @button-clicked="valign = 'bottom'"
            >
              <AligmentBottom />
            </TGroupButton>
          </TButtonGroup>
        </menu-content-container>
        <menu-content-container
          :padding-left-right="8"
          :margin-top="15"
          :border-bottom="false"
        >
          <VueDraggable
            :model-value="cols"
            class="mb-5 space-y-2"
            :animation="150"
            group="columns"
            item-key="network"
            handle=".drag-handle"
            @update="onNewtworkDragEnd"
          >
            <TTabsToggle
              v-for="(col, index) in cols"
              :key="index"
              :header="`${$t('forms.column')} ${(index as number) + 1}`"
              :data-testid="`column-${index}-tab`"
            >
              <template #icon>
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M4 5.25C3.15625 5.25 2.5 5.92969 2.5 6.75V14.25C2.5 15.0938 3.15625 15.75 4 15.75H16C16.8203 15.75 17.5 15.0938 17.5 14.25V6.75C17.5 5.92969 16.8203 5.25 16 5.25H4ZM7 6.75V14.25H4V6.75H7ZM8.5 6.75H11.5V14.25H8.5V6.75ZM16 6.75V14.25H13V6.75H16Z"
                    fill="currentColor"
                  />
                </svg>
              </template>
              <columnSettings :column-index="index as number"></columnSettings>
            </TTabsToggle>
          </VueDraggable>
        </menu-content-container>

        <aiContentInput
          v-if="generalStore.aiLabeledElements"
          target="section"
        ></aiContentInput>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
  import columnSettings from "./elements/columnSettings.vue";
  import borderForm from "./borderForm.vue";
  import hideOnMobile from "./components/hideSectionOnMobile.vue";
  import hideOnDesktop from "./components/hideSectionOnDesktop.vue";
  import borderRadiusForm from "./borderRadiusForm.vue";
  import colorPicker from "./elements/colorPicker.vue";
  import TGradient from "@/components/TGradient/TGradient.vue";

  import ImageButtons from "@/forms/components/ImageButtons/ImageButtons.vue";
  import PaddingForm from "@/forms/paddingForm.vue";
  import aiContentInput from "./aiContentInput.vue";

  import { useNotificationsStore } from "@/stores/notifications";
  import { useRolesStore } from "@/stores/roles";
  import { useMjmlStore } from "@/stores/mjmlStore";
  import { useGeneralStore } from "@/stores/generalStore";
  import { useSectionVariantsStore } from "@/stores/sectionVariantsStore";
  import { useSavedBlocksStore } from "@/stores/savedBlocksStore";

  import {
    ColumnWidth,
    FullWidth,
    Variants,
    AligmentTop,
    AligmentMiddle,
    AligmentBottom,
  } from "@topol/icons";
  import {
    TMergetagsAutocomplete as TAutoComplete,
    TInputNumber,
    TButtonGroup,
    TGroupButton,
    TSwitch,
    TButton,
    TDropdown,
    TNote,
    TTabsToggle,
    TLabel,
    type TDropdownOption,
  } from "@topol/twig-ui";

  import { useBlockAttr } from "@/composables/useBlockAttr";
  import { VueDraggable } from "vue-draggable-plus";
  import { ref, computed, watch } from "vue";

  // eslint-disable-next-line vue/require-prop-types
  const props = defineProps(["section"]);

  const notificationsStore = useNotificationsStore();
  const rolesStore = useRolesStore();
  const mjmlStore = useMjmlStore();
  const generalStore = useGeneralStore();
  const sectionVariantsStore = useSectionVariantsStore();
  const savedBlocksStore = useSavedBlocksStore();

  const { enableSectionVariants } = sectionVariantsStore;

  const { str, nullable, anyPropChanged, resetAttr } = useBlockAttr({
    mode: "section",
  });

  const setDynamic = ref(false);
  const dragKey = ref(1);

  // Composable bindings
  const bgColor = nullable("background-color");
  const padding = str("padding", "0px 0px 0px 0px");
  const borderRadius = nullable("border-radius");
  const backgroundImageSize = str("background-size", "cover");
  const fullWidth = nullable("full-width");
  const inGroup = nullable("in-group");
  const gradient = nullable("gradient");
  const backgroundImage = nullable("background-url");
  const backgroundRepeatBinding = nullable("background-repeat");
  const backgroundRepeat = computed({
    get() {
      return backgroundRepeatBinding.model !== "no-repeat";
    },
    set(val: boolean) {
      backgroundRepeatBinding.model = val ? "repeat" : "no-repeat";
    },
  });
  const border = nullable("border");
  const borderTop = nullable("border-top");
  const borderBottom = nullable("border-bottom");
  const borderLeft = nullable("border-left");
  const borderRight = nullable("border-right");
  const borderAnyChanged = anyPropChanged(
    "border",
    "border-top",
    "border-bottom",
    "border-left",
    "border-right"
  );
  const selectedSection = computed(() => {
    return mjmlStore.getSelectedSectionByIndex();
  });

  const permissions = computed(() => {
    return rolesStore.permissions;
  });

  const canLockSections = computed(() => {
    return permissions.value.canLockSections;
  });

  const canEditLockedSections = computed(() => {
    return permissions.value.canEditLockedSections;
  });

  const isSectionLocked = computed({
    get() {
      const val = mjmlStore.getActiveSectionAttrString("locked");

      if (val === undefined) {
        return false;
      } else {
        return val;
      }
    },

    set() {
      mjmlStore.toggleSectionLock();
    },
  });

  const isSynced = computed(() => {
    if (selectedSection.value && selectedSection.value.syncedId !== undefined)
      return true;

    return false;
  });

  const colsCount = computed({
    get() {
      return mjmlStore.getActiveSectionColumnsCount;
    },
    set(val: number) {
      if (!sectionContainsCarousel()) {
        changeNumberOfColumns(val);
      }
    },
  });

  const cols = computed(() => {
    return mjmlStore.getActiveSectionColumns;
  });

  const valign = computed({
    get() {
      if (
        selectedSection.value &&
        selectedSection.value.children[0].attributes["vertical-align"]
      ) {
        return selectedSection.value.children[0].attributes["vertical-align"];
      }
      return "top";
    },
    set(val: string) {
      for (let i = 0; i < colsCount.value; i++) {
        mjmlStore.updateSpecificColumnAttribute({
          columnIndex: i,
          prop: "vertical-align",
          value: val,
        });
      }
    },
  });

  // When background image is set, encode URI and clear gradient
  watch(
    () => backgroundImage.model,
    (val) => {
      if (!val) return;
      const isEncoded = decodeURI(val) !== val;
      if (!isEncoded) {
        backgroundImage.model = encodeURI(val);
      }
      if (gradient.model) {
        gradient.model = undefined;
      }
    }
  );

  // When gradient is set, clear background image
  watch(
    () => gradient.model,
    (val) => {
      if (val && backgroundImage.model) {
        backgroundImage.model = undefined;
      }
    }
  );

  const stuckOnMobile = computed({
    get() {
      return !inGroup.model;
    },
    set(val: boolean) {
      inGroup.model = !val;
    },
  });

  function addGradient() {
    gradient.model = "linear-gradient(90deg, #ff7e5f 0%, #feb47b 100%)";
  }

  function openVariants() {
    if (!selectedSection.value) return;

    sectionVariantsStore.openSecntionVariants(selectedSection.value.uid);
  }

  function changeNumberOfColumns(columns: number) {
    if (columns < 1 || columns > 8) {
      return;
    }

    if (columns === colsCount.value) {
      return;
    }

    if (columns < colsCount.value) {
      for (let i = colsCount.value; i > columns; i--) {
        mjmlStore.removeColumn();
      }
    } else {
      for (let i = colsCount.value; i < columns; i++) {
        mjmlStore.addColumn();
      }
    }
  }

  function onBackgroundReset() {
    backgroundImage.reset();
    gradient.reset();
  }

  function onBorderReset() {
    for (const prop of [
      "border",
      "border-top",
      "border-bottom",
      "border-left",
      "border-right",
    ]) {
      resetAttr(prop);
    }
  }

  function onBorderChanged({
    border: newBorder,
    borderType = "border",
  }: {
    border: string;
    borderType?: string;
  }) {
    //setting border to undefined when border-top/left/bot/right is set
    //this is set because reactivity isn't working properly when border
    //is set together with border-*direction*
    if (borderType !== "border" && border.model !== undefined) {
      border.model = undefined;
    }

    if (newBorder === undefined || newBorder.startsWith("0px")) {
      mjmlStore.updateSectionAttribute({
        prop: borderType,
        value: undefined,
      });
    } else {
      mjmlStore.updateSectionAttribute({
        prop: borderType,
        value: newBorder,
      });
    }
  }

  function onNewtworkDragEnd(evt: {
    oldIndex: number | undefined;
    newIndex: number | undefined;
  }) {
    if (mjmlStore.selectedSection) {
      mjmlStore.reorderColumns({
        sectionIndex: mjmlStore.selectedSection.sectionIndex,
        oldIndex: evt.oldIndex,
        newIndex: evt.newIndex,
      });
      dragKey.value += 1;
    }
  }

  function sectionContainsCarousel() {
    const selectedSection = mjmlStore.getSelectedSectionByIndex(
      props.section.index
    );

    if (selectedSection && selectedSection.children) {
      for (const column of selectedSection.children) {
        if (column.children) {
          for (const block of column.children) {
            if (block.tagName === "mj-carousel") {
              notificationsStore.createNotification({
                type: "error",
                text: "Structure with Carousel Block can only be contained in one column layout.",
              });

              return true;
            }
          }
        }
      }
    }

    return false;
  }
</script>

<style>
  .lock-wrapper {
    position: relative;
    justify-content: center;
  }

  .button-wrapper {
    position: absolute;
    z-index: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    top: 64px;
  }

  .icon-style {
    width: 20px;
    height: 20px;
  }

  .locked {
    background-color: var(--c-g700);
    opacity: 0.05;
    padding-top: 0px;
    z-index: -1;
    height: 100%;
    pointer-events: none;
    display: flex;
    justify-content: center;
  }
</style>
